import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useIsMountedState, useMeasureObserver, useRefs, useSetStyles, useSpring, useIsomorphicLayoutEffect, useDrag } from '@unlikelystudio/react-hooks';
import { gsap } from 'gsap';
import { useInView } from 'react-intersection-observer';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArray(to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
}

function useSliderState$1() {
    return useState({
        addOffset: function () { },
        setOffset: function () { },
        dragState: null,
        defaultBounds: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
        },
        setSlideIndex: function () { },
        nextSlide: function () { },
        prevSlide: function () { },
        slideIndex: 0,
    });
}

function clamp(num, min, max) {
    return Math.min(Math.max(num, min), max);
}
function useSliderState(_a) {
    var length = _a.length, infinite = _a.infinite, _b = _a.initialSlideIndex, initialSlideIndex = _b === void 0 ? 0 : _b;
    var maxIndex = useMemo(function () {
        return length - 1;
    }, [length]);
    var _c = useState(clamp(initialSlideIndex, 0, maxIndex)), slideIndex = _c[0], setSlideIndex = _c[1];
    var nextSlide = useCallback(function () {
        return setSlideIndex(function (prevSlideIndex) {
            var targetedSlideIndex = prevSlideIndex + 1;
            if (infinite) {
                return targetedSlideIndex > maxIndex ? 0 : targetedSlideIndex;
            }
            else {
                return Math.min(targetedSlideIndex, maxIndex);
            }
        });
    }, [length, infinite]);
    var prevSlide = useCallback(function () {
        return setSlideIndex(function (prevSlideIndex) {
            var targetedSlideIndex = prevSlideIndex - 1;
            if (infinite) {
                return targetedSlideIndex < 0 ? maxIndex : targetedSlideIndex;
            }
            else {
                return Math.max(targetedSlideIndex, 0);
            }
        });
    }, [length, infinite]);
    return [slideIndex, setSlideIndex, nextSlide, prevSlide];
}

function multipleOf(num, mul) {
    return num && mul ? Math.round(num / mul) * mul : num;
}
function getCopyCount(sliderDimension, sliderContainerDimension) {
    if (sliderDimension && sliderContainerDimension) {
        return Math.ceil(sliderDimension / sliderContainerDimension);
    }
    else {
        return 2;
    }
}
function pxStringToInt(pxString) {
    return parseInt(pxString.split('px')[0]);
}
function shorterDirection(currentIndex, targetIndex, length) {
    var toRight = (targetIndex - currentIndex + length) % length;
    var toLeft = (currentIndex - targetIndex + length) % length;
    return toRight > toLeft ? -toLeft : toRight;
}
function Slider(_a) {
    var _b;
    var _c, _d;
    var dragProps = _a.dragProps, _e = _a.axis, axis = _e === void 0 ? 'x' : _e, snap = _a.snap, setSliderState = _a.setSliderState, _f = _a.springConfig, springConfig = _f === void 0 ? {} : _f, _g = _a.touchSpringConfig, touchSpringConfig = _g === void 0 ? { friction: 2 } : _g, _h = _a.excludeMarginFromDefaultBounds, excludeMarginFromDefaultBounds = _h === void 0 ? true : _h, _j = _a.infinite, infinite = _j === void 0 ? false : _j, className = _a.className, containerClassName = _a.containerClassName, smallSliderClassName = _a.smallSliderClassName, children = _a.children, customSliderRef = _a.customSliderRef, onSlideIndexChange = _a.onSlideIndexChange, _k = _a.initialSlideIndex, initialSlideIndex = _k === void 0 ? 0 : _k, _l = _a.autoPlay, autoPlay = _l === void 0 ? {
        speed: 0,
    } : _l, style = _a.style;
    var isMounted = useIsMountedState();
    var slideIndex = useRef(0);
    var length = (_c = children === null || children === void 0 ? void 0 : children.length) !== null && _c !== void 0 ? _c : 0;
    var sliderRef = useRef();
    var containerRef = useRef();
    var axisToUppercase = axis.toUpperCase();
    var _m = useInView(), setInViewRef = _m[0], inView = _m[1];
    var setSliderRef = useCallback(function (node) {
        setInViewRef(node);
        sliderRef.current = node;
    }, []);
    var _o = useState({
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    }), margin = _o[0], setMargin = _o[1];
    var dimension;
    var position;
    if (axis === 'x') {
        dimension = 'width';
        position = 'left';
    }
    else {
        dimension = 'height';
        position = 'top';
    }
    // Get slider root element and slider container dimensions
    var sliderContainerDimension = useMeasureObserver(containerRef);
    var sliderDimension = useMeasureObserver(sliderRef, 'getBoundingClientRect');
    var customSliderDimension = useMeasureObserver(customSliderRef, 'getBoundingClientRect');
    var startOffset = customSliderRef
        ? sliderDimension[position] - customSliderDimension[position]
        : 0;
    // Compute how many copies of the slider we need to make the slider appear infinite
    var copyCount = infinite
        ? getCopyCount(customSliderRef
            ? customSliderDimension[dimension]
            : sliderDimension[dimension], sliderContainerDimension[dimension])
        : 0;
    // Compute max slider dimensions with copies
    var maxSliderContainersDimension = sliderContainerDimension[dimension] * copyCount;
    // Store refs of copies of the slider
    var sliderContainerCopyRefs = useRefs(copyCount);
    // Generate style setter for slider
    var refs = { current: __spreadArray([containerRef], sliderContainerCopyRefs.current) };
    var setStyles = useSetStyles(refs, null, [copyCount]);
    var getSlideIndexFromDragOffset = function (dragOffset) {
        var preComputedIndex = Math.round((-dragOffset / sliderContainerDimension[dimension]) * length);
        var index = infinite
            ? gsap.utils.wrap(0, length, preComputedIndex)
            : gsap.utils.clamp(0, length - 1, preComputedIndex);
        return sliderContainerDimension[dimension] ? index : 0;
    };
    var defaultBounds = useMemo(function () {
        return {
            top: sliderDimension.height -
                sliderContainerDimension.height +
                margin.bottom,
            right: -margin.left,
            bottom: -margin.top,
            left: sliderDimension.width - sliderContainerDimension.width + margin.right,
        };
    }, [sliderDimension, sliderContainerDimension, margin]);
    // Compute if slides are larger than the visible slider container
    var sliderContainerIsLargerThanSlider = sliderContainerDimension[dimension] -
        (axis === 'x' ? margin.left + margin.right : margin.top + margin.bottom) >
        sliderDimension[dimension];
    var enableDrag = (dragProps === null || dragProps === void 0 ? void 0 : dragProps.enable) === false
        ? false
        : infinite || sliderContainerIsLargerThanSlider;
    var _p = useSpring((_b = {},
        _b[axis] = 0,
        _b.config = __assign({ friction: 10, perfectStop: true, precisionStop: 0.01, interpolation: 'linear' }, springConfig),
        _b.onUpdate = function (xy) {
            var _a, _b;
            if ((autoPlay === null || autoPlay === void 0 ? void 0 : autoPlay.speed) && !dragState.current.down) {
                addOffset([
                    axis === 'x' ? autoPlay === null || autoPlay === void 0 ? void 0 : autoPlay.speed : 0,
                    axis === 'y' ? autoPlay === null || autoPlay === void 0 ? void 0 : autoPlay.speed : 0,
                ]);
            }
            if (inView) {
                (_b = (_a = setStyles === null || setStyles === void 0 ? void 0 : setStyles.current) === null || _a === void 0 ? void 0 : _a.forEach) === null || _b === void 0 ? void 0 : _b.call(_a, function (setStyle, index) {
                    var _a;
                    setStyle((_a = {
                            cursor: enableDrag
                                ? dragState.current.down
                                    ? 'grabbing'
                                    : 'grab'
                                : 'auto'
                        },
                        _a[axis] = function () {
                            return infinite
                                ? gsap.utils.wrap(-sliderContainerDimension[dimension] - startOffset, maxSliderContainersDimension - startOffset, xy[axis] + sliderContainerDimension[dimension] * index)
                                : xy[axis];
                        },
                        _a));
                });
            }
        },
        _b)), setSpring = _p[0], inrtia = _p[3];
    //Set slider to the correct initial position
    var firstReset = useRef(true);
    useIsomorphicLayoutEffect(function () {
        if (firstReset.current && sliderContainerDimension[dimension]) {
            inrtia.current.value[axis] =
                -(sliderContainerDimension[dimension] / length) * initialSlideIndex;
            firstReset.current = false;
        }
    }, [sliderContainerDimension]);
    // Function to compute the default snap offset of the slider
    var snapOffset = function (dragOffset) {
        var x = dragOffset[0], y = dragOffset[1];
        var newDragOffset = [
            axis === 'x'
                ? multipleOf(x, sliderContainerDimension[dimension] / length)
                : x,
            axis === 'y'
                ? multipleOf(y, sliderContainerDimension[dimension] / length)
                : y,
        ];
        return newDragOffset;
    };
    var _q = useSliderState({ initialSlideIndex: initialSlideIndex, length: length, infinite: infinite }), targetSlideIndex = _q[0], setSlideIndex = _q[1], nextSlide = _q[2], prevSlide = _q[3];
    var onDrag = function (_a) {
        var offsetWithRubberband = _a.offsetWithRubberband, offset = _a.offset, down = _a.down, touches = _a.touches;
        var values;
        if (infinite) {
            values = {
                x: offset[0],
                y: offset[1],
            };
        }
        else {
            values = {
                x: offsetWithRubberband[0],
                y: offsetWithRubberband[1],
            };
        }
        var currentSlideIndex = getSlideIndexFromDragOffset(values[axis]);
        if (currentSlideIndex !== slideIndex.current) {
            slideIndex.current = currentSlideIndex;
            setSlideIndex(currentSlideIndex);
            onSlideIndexChange === null || onSlideIndexChange === void 0 ? void 0 : onSlideIndexChange(currentSlideIndex);
        }
        setSpring(__assign(__assign({}, values), { config: __assign({ friction: down ? 5 : 8 }, (touches && down ? __assign({}, touchSpringConfig) : __assign({}, springConfig))) }));
    };
    useEffect(function () {
        onSlideIndexChange === null || onSlideIndexChange === void 0 ? void 0 : onSlideIndexChange(targetSlideIndex);
    }, [targetSlideIndex]);
    var _r = useDrag(__assign({ ref: sliderRef, snap: snap ? snapOffset : null, onDrag: onDrag, throwFactor: [200, 200], rubberband: 0.55, enable: enableDrag, bounds: infinite ? undefined : defaultBounds, axis: axis }, dragProps)), setOffset = _r.setOffset, addOffset = _r.addOffset, dragState = _r.dragState;
    // Reset slider offset if the slider in smaller than his container
    useIsomorphicLayoutEffect(function () {
        if (!sliderContainerIsLargerThanSlider) {
            setOffset([0, 0]);
        }
    }, [sliderContainerIsLargerThanSlider]);
    //Auto detect margins to adjust bounds
    useIsomorphicLayoutEffect(function () {
        var _a;
        if (excludeMarginFromDefaultBounds && !infinite) {
            var children_1 = containerRef.current.children;
            var firstChild = children_1[0];
            var lastChild = (_a = children_1[length - 1]) !== null && _a !== void 0 ? _a : null;
            var firstChildStyle = getComputedStyle(firstChild);
            var lastChildStyle = lastChild
                ? getComputedStyle(lastChild)
                : firstChildStyle;
            setMargin({
                top: pxStringToInt(firstChildStyle.marginTop),
                left: pxStringToInt(firstChildStyle.marginLeft),
                right: pxStringToInt(lastChildStyle.marginRight),
                bottom: pxStringToInt(lastChildStyle.marginBottom),
            });
        }
    }, [sliderContainerDimension, excludeMarginFromDefaultBounds, infinite]);
    // Move to the current slide index
    useIsomorphicLayoutEffect(function () {
        if (typeof targetSlideIndex === 'number' &&
            sliderContainerDimension[dimension] !== 0 &&
            sliderContainerIsLargerThanSlider) {
            var slideOffset = shorterDirection(slideIndex.current, targetSlideIndex, length);
            var offsetToApply = (sliderContainerDimension[dimension] / length) * slideOffset;
            addOffset([
                axis === 'x' ? -offsetToApply : 0,
                axis === 'y' ? -offsetToApply : 0,
            ]);
        }
    }, [
        targetSlideIndex,
        sliderContainerDimension,
        length,
        sliderContainerIsLargerThanSlider,
    ]);
    useIsomorphicLayoutEffect(function () {
        setSliderState === null || setSliderState === void 0 ? void 0 : setSliderState({
            addOffset: addOffset,
            setOffset: setOffset,
            dragState: dragState,
            defaultBounds: defaultBounds,
            setSlideIndex: setSlideIndex,
            nextSlide: nextSlide,
            prevSlide: prevSlide,
            slideIndex: targetSlideIndex,
        });
    }, [defaultBounds, setSlideIndex, nextSlide, prevSlide, targetSlideIndex]);
    var ssrOffset = (targetSlideIndex / length) * 100;
    var transformString = "translate" + axisToUppercase;
    var defaultContainerStyle = {
        flexDirection: axis === 'x' ? 'row' : 'column',
        cursor: (dragProps === null || dragProps === void 0 ? void 0 : dragProps.enable) ? 'grab' : null,
        display: 'inline-flex',
        willChange: 'transform',
    };
    return (React.createElement("div", { ref: setSliderRef, style: __assign(__assign({}, style), { position: 'relative', overflow: customSliderRef ? 'visible' : 'hidden', touchAction: axis === 'x' ? 'pan-y' : 'pan-x' }), className: "" + (className ? className : '') + (smallSliderClassName && !sliderContainerIsLargerThanSlider
            ? " " + smallSliderClassName
            : '') },
        React.createElement("div", { ref: containerRef, className: containerClassName, style: __assign({ transform: !isMounted ? transformString + "(" + -ssrOffset + "%)" : null }, defaultContainerStyle) }, children), (_d = sliderContainerCopyRefs === null || sliderContainerCopyRefs === void 0 ? void 0 : sliderContainerCopyRefs.current) === null || _d === void 0 ? void 0 :
        _d.map(function (ref, index) {
            return (React.createElement("div", { ref: ref, key: index, style: __assign(__assign({}, defaultContainerStyle), { position: 'absolute', top: '0px', left: '0px', transform: index === 0 && !isMounted
                        ? transformString + "(-" + (100 + ssrOffset) + "%)"
                        : index === 1 && !isMounted
                            ? transformString + "(" + (100 - ssrOffset) + "%)"
                            : null }) }, children));
        })));
}

export default Slider;
export { useSliderState$1 as useSliderState };
//# sourceMappingURL=index.es.js.map
